import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { API_BASE_URL } from '../api/axiosConfig';
import { getAccessTokenAsync } from '../service/tokenService';

// WebSocket 기능을 제공할 Context 타입 정의
interface WebsocketContextType {
  subscribe: (topic: string, callback: (message: any) => void) => void;
  unsubscribe: (topic: string) => void;
  publish: (destination: string, body?: any) => void;
  connectSocket: () => Promise<void>;
  isConnected: boolean;
}

// Context 생성
const WebsocketContext = createContext<WebsocketContextType | null>(null);

// Provider 컴포넌트
export const WebsocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const stompClientRef = useRef<Client | null>(null);
  const subscriptions = useRef<{ [key: string]: any }>({});

  // const isConnected = useRef(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  let stompClient: Client | null = null;

  //   useEffect(() => {
  //     connectSocket();
  //     return () => disconnectSocket();
  //   }, []);

  // WebSocket 연결 함수
  async function connectSocket(): Promise<void> {
    let accessToken = await getAccessTokenAsync();
    if (!accessToken) return;

    const socket = new SockJS(`${API_BASE_URL}/ws`);

    return new Promise((resolve, reject) => {
      stompClient = new Client({
        webSocketFactory: () => socket,
        connectHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
        onConnect: () => {
          stompClientRef.current = stompClient;
          // isConnected.current = true;
          setIsConnected(true);
          resolve(); // WebSocket 연결 후 resolve 호출
        },
        onStompError: (frame) => {
          console.error('WebSocket Error:', frame.headers['message']);
          // isConnected.current = false;
          setIsConnected(false);
          reject(new Error(frame.headers['message'])); // 에러 발생 시 reject 호출
        },
      });
      console.log(stompClient);
      stompClient.activate();
    });
  }

  // 구독 함수
  function subscribe(topic: string, callback: (message: any) => void) {
    if (!stompClientRef.current) return;
    if (subscriptions.current[topic]) {
      console.log('subscribed already');
      return;
    } // 이미 구독한 경우 무시

    const subscription = stompClientRef.current.subscribe(
      `${topic}`,
      (message) => {
        callback(JSON.parse(message.body));
      }
    );

    subscriptions.current[topic] = subscription;
  }

  // 구독 해제 함수
  function unsubscribe(topic: string) {
    if (subscriptions.current[topic]) {
      subscriptions.current[topic].unsubscribe();
      delete subscriptions.current[topic];
    }
  }

  // 메시지 발행 함수
  function publish(destination: string, body?: any) {
    if (stompClientRef.current) {
      stompClientRef.current.publish({
        destination: `${destination}`,
        body: body ? JSON.stringify(body) : undefined,
      });
    }
  }

  // WebSocket 종료 함수
  function disconnectSocket() {
    Object.keys(subscriptions.current).forEach(unsubscribe);
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
      // isConnected.current = false;
      setIsConnected(false);
    }
  }

  return (
    <WebsocketContext.Provider
      value={{
        subscribe,
        unsubscribe,
        publish,
        connectSocket,
        // isConnected: isConnected.current,
        isConnected: isConnected,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};

// WebSocket Context를 쉽게 사용하기 위한 훅
export const useWebsocket = () => {
  const context = useContext(WebsocketContext);
  if (!context)
    throw new Error('useWebsocket must be used within a WebsocketProvider');
  return context;
};
